import React, { useState, memo } from 'react';
import {
  Layers,
  Search,
  ChevronUp,
  ChevronDown,
  X,
  Filter,
  Eye,
  CheckSquare,
  Flag,
  User2,
  Sparkles,
  Calendar,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { FullProject } from '../../types';

export interface AppHeaderSearchBarProps {
  searchDropdownRef: React.RefObject<HTMLDivElement | null>;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: any[];
  searchIndex: number;
  setSearchIndex: (index: number) => void;
  searchMode: 'filter' | 'highlight';
  setSearchMode: (mode: 'filter' | 'highlight') => void;
  searchCategory: string;
  setSearchCategory: (category: any) => void;
  showSearchDropdown: boolean;
  setShowSearchDropdown: (show: boolean) => void;
  handleSearchPrev: () => void;
  handleSearchNext: () => void;
  navigateToSearchResult: (index: number) => void;
  setActiveSearchTaskId: (id: string | null) => void;
  lastNavigatedIndexRef: React.MutableRefObject<number>;
  allProjects: FullProject[];
}

export const AppHeaderSearchBar: React.FC<AppHeaderSearchBarProps> = memo(({
  searchDropdownRef,
  searchQuery,
  setSearchQuery,
  searchResults,
  searchIndex,
  setSearchIndex,
  searchMode,
  setSearchMode,
  searchCategory,
  setSearchCategory,
  showSearchDropdown,
  setShowSearchDropdown,
  handleSearchPrev,
  handleSearchNext,
  navigateToSearchResult,
  setActiveSearchTaskId,
  lastNavigatedIndexRef,
  allProjects,
}) => {
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  return (
    <div className="relative" ref={searchDropdownRef} data-tour="global-search">
      <div
        className={`flex items-center bg-white/10 dark:bg-black/40 px-4 py-2 rounded-2xl border transition-[border-color,box-shadow] duration-200 w-72 lg:w-[32rem] shrink-0 shadow-2xl ${
          searchQuery
            ? 'border-amber-400/50 shadow-amber-500/20 ring-2 ring-amber-500/10'
            : `border-white/10 ${isSearchFocused ? 'border-indigo-500/50' : ''}`
        }`}
      >
        <Search size={16} className={searchQuery ? 'text-amber-400 animate-pulse' : 'text-slate-400'} />
        <input
          type="text"
          placeholder="Suche..."
          value={searchQuery}
          onFocus={() => setIsSearchFocused(true)}
          onBlur={() => {
            setTimeout(() => {
              setIsSearchFocused(false);
            }, 150);
          }}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setSearchIndex(0);
            setActiveSearchTaskId(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (searchResults.length > 0) {
                if (e.shiftKey) {
                  handleSearchPrev();
                } else {
                  handleSearchNext();
                }
              }
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              if (searchResults.length > 0) {
                let nextIndex = 0;
                if (lastNavigatedIndexRef.current !== -1) {
                  nextIndex = (searchIndex + 1) % searchResults.length;
                }
                navigateToSearchResult(nextIndex);
              }
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              if (searchResults.length > 0) {
                let prevIndex = searchResults.length - 1;
                if (lastNavigatedIndexRef.current !== -1) {
                  prevIndex = (searchIndex - 1 + searchResults.length) % searchResults.length;
                }
                navigateToSearchResult(prevIndex);
              }
            }
          }}
          className="bg-transparent border-none outline-none text-sm ml-2 flex-1 min-w-0 placeholder:text-slate-500 text-white font-bold tracking-tight"
        />
        {searchQuery && (
          <div className="flex items-center gap-1 pl-2 border-l border-white/10 shrink-0 mr-1">
            <div className="flex items-center gap-1 px-1.5 py-0.5 bg-indigo-500/20 rounded-md border border-indigo-500/30">
              <span className="text-[10px] font-black text-indigo-300 tabular-nums uppercase tracking-tighter">
                {searchResults.length > 0 ? searchIndex + 1 : 0}/{searchResults.length}
              </span>
            </div>
            <button onClick={handleSearchPrev} className="p-1 hover:bg-white/10 rounded-md text-slate-400" title="Vorheriges">
              <ChevronUp size={14} />
            </button>
            <button onClick={handleSearchNext} className="p-1 hover:bg-white/10 rounded-md text-slate-400" title="Nächstes">
              <ChevronDown size={14} />
            </button>
            <button
              onClick={() => {
                setSearchQuery('');
                setActiveSearchTaskId(null);
              }}
              className="p-0.5 hover:bg-white/10 rounded-md text-rose-400"
              title="Suche löschen"
            >
              <X size={14} />
            </button>
          </div>
        )}

        <div className="h-4 w-px bg-white/10 mx-2 shrink-0" />

        {/* Search Mode Toggle (Filtering vs Highlighting) */}
        <button
          onClick={() => setSearchMode(searchMode === 'filter' ? 'highlight' : 'filter')}
          className="p-1.5 rounded-xl border transition-all active:scale-95 flex items-center justify-center shrink-0 w-8 h-8 bg-white/5 border-white/10 text-slate-400 hover:text-white hover:bg-white/10"
          title={
            searchMode === 'filter'
              ? 'Suchmodus: Filtern (andere Aufgaben ausblenden)'
              : 'Suchmodus: Hervorheben (andere Aufgaben dimmen)'
          }
        >
          {searchMode === 'filter' ? <Filter size={14} /> : <Eye size={14} />}
        </button>

        <div className="h-4 w-px bg-white/10 mx-2 shrink-0" />

        {/* Custom Dropdown Selection */}
        <div className="relative shrink-0 flex items-center">
          <button
            onClick={() => setShowSearchDropdown(!showSearchDropdown)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-xl border transition-all hover:bg-white/10 active:scale-95 text-[10px] font-black uppercase tracking-wider ${
              showSearchDropdown ? 'bg-white/10 border-indigo-500/50 text-white' : 'bg-white/5 border-white/10 text-slate-350'
            }`}
            title="Kategorie filtern"
          >
            <span>
              {searchCategory === 'all'
                ? 'Alle'
                : searchCategory === 'task'
                ? 'Aufgaben'
                : searchCategory === 'milestone'
                ? 'Meilensteine'
                : searchCategory === 'person'
                ? 'Personen'
                : 'KI-Hub'}
            </span>
            <ChevronDown
              size={10}
              className={`text-slate-400 transition-transform duration-200 ${showSearchDropdown ? 'rotate-180' : ''}`}
            />
          </button>

          <AnimatePresence>
            {showSearchDropdown && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                className="absolute right-0 top-full mt-2 w-48 bg-slate-900 border border-white/10 rounded-2xl shadow-2xl p-1.5 z-[100] backdrop-blur-xl"
              >
                {[
                  { id: 'all', label: 'Alle Bereiche', icon: Layers },
                  { id: 'task', label: 'Aufgaben', icon: CheckSquare },
                  { id: 'milestone', label: 'Meilensteine', icon: Flag },
                  { id: 'person', label: 'Personen', icon: User2 },
                  { id: 'inbox', label: 'KI-Hub (Eingang)', icon: Sparkles },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => {
                      setSearchCategory(opt.id as any);
                      setSearchIndex(0);
                      setActiveSearchTaskId(null);
                      setShowSearchDropdown(false);
                    }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-[11px] font-bold rounded-xl transition-all ${
                      searchCategory === opt.id
                        ? 'bg-indigo-600 text-white shadow-md'
                        : 'text-slate-400 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <opt.icon size={13} />
                    <span>{opt.label}</span>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Spotlight-style Search Results dropdown list */}
      {isSearchFocused && searchQuery && searchResults.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-slate-950/90 border border-white/10 rounded-2xl shadow-2xl p-2 z-[200] backdrop-blur-xl max-h-80 overflow-y-auto custom-scrollbar flex flex-col gap-1">
          {searchResults.map((item, idx) => {
            const isItemActive = idx === searchIndex;
            const Icon =
              item.type === 'project'
                ? Layers
                : item.category === 'Knotenpunkt' || item.type === 'contact'
                ? User2
                : item.type === 'memo'
                ? Sparkles
                : item.type === 'milestone'
                ? Flag
                : item.type === 'holiday' || item.category === 'Feiertag'
                ? Calendar
                : CheckSquare;

            const displayType =
              item.type === 'project'
                ? 'Projekt'
                : item.category === 'Knotenpunkt' || item.type === 'contact'
                ? 'Person'
                : item.type === 'memo'
                ? 'KI-Hub'
                : item.type === 'milestone'
                ? 'Meilenstein'
                : item.type === 'holiday' || item.category === 'Feiertag'
                ? 'Feiertag'
                : 'Aufgabe';

            const proj = allProjects.find((p) => p.id === item.projectId);
            const projName = proj ? proj.name : item.projectId === 'inbox_inforum' ? 'Nicht zugeordnet' : '';

            let areaName = '';
            if (item.category === 'Knotenpunkt' || item.type === 'contact') {
              areaName = 'Netzwerk';
            } else if (item.category === 'Eingang') {
              areaName = 'KI-Hub (Eingang)';
            } else if (item.category === 'Chronik') {
              areaName = 'KI-Hub (Chronik)';
            } else if (item.type === 'memo') {
              areaName = 'KI-Hub';
            } else if (projName) {
              areaName = `Projekt: ${projName}`;
            } else {
              areaName = 'Nicht zugeordnet';
            }

            const showAssignee = searchCategory === 'person' && item.assignee && item.name;
            const displayName = showAssignee
              ? `${item.name} (${item.assignee})`
              : item.name ||
                (item.type === 'contact' || item.type === 'meeting' || item.category === 'Kommunikation'
                  ? item.assignee
                  : '') ||
                'Unbenannt';

            const qNormal = searchQuery.trim().toLowerCase();
            const matchesTitle = qNormal && item.name?.toLowerCase().includes(qNormal);
            const matchesAssignee = qNormal && item.assignee?.toLowerCase().includes(qNormal);
            const matchesDetails =
              qNormal &&
              ((item.subtitle || '').toLowerCase().includes(qNormal) ||
                (item.description || '').toLowerCase().includes(qNormal) ||
                (item.organization || '').toLowerCase().includes(qNormal));

            return (
              <button
                key={item.id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  navigateToSearchResult(idx);
                }}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-xl transition-all text-left ${
                  isItemActive
                    ? 'bg-gradient-to-r from-orange-500/20 to-amber-500/10 border border-orange-500/30 text-orange-200 shadow-lg shadow-orange-500/5'
                    : 'hover:bg-white/5 border border-transparent text-slate-350'
                }`}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <Icon size={14} className={isItemActive ? 'text-orange-400 animate-pulse' : 'text-slate-400'} />
                  <div className="flex flex-col min-w-0">
                    <span className="text-[11px] font-bold truncate leading-snug">{displayName}</span>
                    {areaName && (
                      <span className="text-[9px] text-slate-500 font-bold uppercase tracking-tight">{areaName}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0 pl-2">
                  {matchesTitle && (
                    <span
                      className={`text-[7px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md border ${
                        isItemActive
                          ? 'bg-indigo-500/25 border-indigo-500/30 text-indigo-300'
                          : 'bg-indigo-950/40 border-indigo-900/30 text-indigo-400'
                      }`}
                    >
                      Titel
                    </span>
                  )}
                  {matchesAssignee && (
                    <span
                      className={`text-[7px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md border ${
                        isItemActive
                          ? 'bg-emerald-500/25 border-emerald-500/30 text-emerald-300'
                          : 'bg-emerald-950/40 border-emerald-900/30 text-emerald-400'
                      }`}
                    >
                      Person
                    </span>
                  )}
                  {matchesDetails && (
                    <span
                      className={`text-[7px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md border ${
                        isItemActive
                          ? 'bg-slate-500/25 border-slate-500/30 text-slate-300'
                          : 'bg-slate-900/40 border-slate-800/30 text-slate-400'
                      }`}
                    >
                      Details
                    </span>
                  )}
                  <span
                    className={`text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md border ${
                      isItemActive
                        ? 'bg-orange-500/25 border-orange-500/30 text-orange-300'
                        : 'bg-slate-800 border-white/5 text-slate-400'
                    }`}
                  >
                    {displayType}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
});
